import { Component, computed, effect, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { formatDateOnly, formatTimeOfDay } from '../../core/datetime/ist';
import { AuthService } from '../../core/services/auth.service';
import { ComplaintService } from '../../core/services/complaint.service';
import { HearingRecord, OpsService } from '../../core/services/ops.service';

type Filter = 'all' | 'Scheduled' | 'Completed' | 'Cancelled' | 'Rescheduled';

@Component({
  selector: 'app-hearing-management',
  standalone: true,
  imports: [FormsModule, RouterLink],
  templateUrl: './hearing-management.component.html',
  styleUrl: './hearing-management.component.scss',
})
export class HearingManagementComponent {
  private readonly ops = inject(OpsService);
  private readonly auth = inject(AuthService);
  private readonly complaints = inject(ComplaintService);
  private readonly route = inject(ActivatedRoute);

  readonly filter = signal<Filter>('all');
  readonly selectedId = signal<string | number | null>(null);
  readonly notes = signal('');
  readonly message = signal<string | null>(null);
  readonly busy = signal(false);

  readonly canConduct = computed(() => this.auth.hasRole('dm', 'admin'));

  readonly hearings = computed(() => {
    const list = [...this.ops.hearingList()].sort((a, b) =>
      String(b.scheduledAt).localeCompare(String(a.scheduledAt)),
    );
    const f = this.filter();
    if (f === 'all') return list;
    return list.filter((h) => h.status === f);
  });

  readonly selected = computed((): HearingRecord | null => {
    const list = this.hearings();
    if (!list.length) return null;
    const id = this.selectedId();
    return (id != null ? list.find((h) => String(h.id) === String(id)) : null) ?? list[0];
  });

  /** All sittings for the selected case (oldest → newest). */
  readonly caseHearings = computed(() => {
    const h = this.selected();
    if (!h) return [] as HearingRecord[];
    return this.ops.hearingsForCase(h.caseNo);
  });

  readonly selectedSeq = computed(() => this.ops.hearingSequence(this.selected()));

  readonly hasOpenSibling = computed(() =>
    this.caseHearings().some((x) => x.status === 'Scheduled' || x.status === 'Rescheduled'),
  );

  readonly selectedComplaint = computed(() => {
    const h = this.selected();
    if (!h) return null;
    return this.complaints.list().find((c) => String(c.id) === String(h.caseNo)) || null;
  });

  readonly counts = computed(() => {
    const all = this.ops.hearingList();
    const casesWithMulti = new Set(
      all
        .map((h) => String(h.caseNo))
        .filter((caseNo, _i, arr) => arr.filter((c) => c === caseNo).length > 1),
    );
    return {
      total: all.length,
      scheduled: all.filter((h) => h.status === 'Scheduled').length,
      completed: all.filter((h) => h.status === 'Completed').length,
      cancelled: all.filter((h) => h.status === 'Cancelled').length,
      multiCase: casesWithMulti.size,
    };
  });

  constructor() {
    const caseId = this.route.snapshot.queryParamMap.get('case');
    if (caseId) {
      effect(() => {
        const forCase = this.ops.hearingsForCase(caseId);
        if (!forCase.length) return;
        // Prefer an open sitting; otherwise the latest hearing for the case.
        const preferred =
          forCase.find((h) => h.status === 'Scheduled' || h.status === 'Rescheduled')
          ?? forCase[forCase.length - 1];
        if (preferred && String(this.selectedId()) !== String(preferred.id)) {
          this.selectedId.set(preferred.id);
        }
      });
    }
  }

  seqFor(h: HearingRecord): { index: number; total: number } {
    return this.ops.hearingSequence(h);
  }

  isSelected(h: HearingRecord): boolean {
    return String(this.selected()?.id) === String(h.id);
  }

  formatSlot(h: HearingRecord | null): string {
    if (!h) return '—';
    const date = formatDateOnly(h.hearingDate || (h.scheduledAt || '').slice(0, 10));
    const time = formatTimeOfDay(h.hearingTime || (h.scheduledAt || '').slice(11, 16));
    if (date !== '—' && time !== '—') return `${date} · ${time}`;
    return h.scheduledAt || '—';
  }

  formatDate(h: HearingRecord | null): string {
    if (!h) return '—';
    return formatDateOnly(h.hearingDate || (h.scheduledAt || '').slice(0, 10));
  }

  formatTime(h: HearingRecord | null): string {
    if (!h) return '—';
    return formatTimeOfDay(h.hearingTime || (h.scheduledAt || '').slice(11, 16));
  }

  statusTone(status: string): string {
    if (status === 'Completed') return 'tone-ready';
    if (status === 'Rescheduled') return 'tone-wait';
    if (status === 'Cancelled') return 'tone-neutral';
    return 'tone-info';
  }

  select(id: string | number): void {
    this.selectedId.set(id);
    this.notes.set('');
  }

  setFilter(f: Filter): void {
    this.filter.set(f);
    this.selectedId.set(null);
  }

  async markHeard(): Promise<void> {
    if (!this.canConduct()) {
      this.message.set('Only DM can mark hearing conducted.');
      return;
    }
    const h = this.selected();
    if (!h) return;
    this.busy.set(true);
    try {
      this.message.set(await this.ops.markHeard(h.caseNo, this.notes(), h.id));
      this.notes.set('');
      this.selectedId.set(h.id);
    } catch (err) {
      this.message.set(err instanceof Error ? err.message : 'Mark heard failed');
    } finally {
      this.busy.set(false);
    }
  }

  async cancel(): Promise<void> {
    if (!this.canConduct()) {
      this.message.set('Only DM / Admin can cancel a hearing.');
      return;
    }
    const h = this.selected();
    if (!h) return;
    this.busy.set(true);
    try {
      this.message.set(await this.ops.cancelHearing(h.id));
    } catch (err) {
      this.message.set(err instanceof Error ? err.message : 'Cancel failed');
    } finally {
      this.busy.set(false);
    }
  }

  async requestAnotherHearing(): Promise<void> {
    if (!this.canConduct()) {
      this.message.set('Only DM can request another hearing.');
      return;
    }
    const h = this.selected();
    if (!h) return;
    this.busy.set(true);
    try {
      this.message.set(
        await this.ops.requestHearing(
          h.caseNo,
          this.notes() || 'Further hearing required for this case',
        ),
      );
      this.notes.set('');
    } catch (err) {
      this.message.set(err instanceof Error ? err.message : 'Request hearing failed');
    } finally {
      this.busy.set(false);
    }
  }
}
